import { default as MuiFormControl, FormControlProps } from '@mui/material/FormControl'

import { styled, uiTokens } from '../../theme'
import { createComponentWrapperWithRef, THEME_CONST } from '../../theme'
import { COLORS } from '../../theme/colors'
import { MsdkStyledComponent } from '../../utils'

type MsdkFormFieldProps = FormControlProps & {
  $hasPrefix?: boolean
  severity?: keyof typeof COLORS.validation
  readOnly?: boolean
}

const MuiFormField: MsdkStyledComponent<MsdkFormFieldProps> = styled(MuiFormControl, {
  shouldForwardProp: (prop) => prop !== '$hasPrefix' && prop !== 'severity' && prop !== 'readOnly',
})<{
  $hasPrefix?: boolean
  severity?: keyof typeof COLORS.validation
  readOnly?: boolean
}>`
  width: 100%;
  padding-right: 16px;

  > .MuiOutlinedInput-root,
  > * > .MuiOutlinedInput-root,
  > * > * > .MuiOutlinedInput-root {
    border-radius: ${THEME_CONST.inputBorderRadius}px;

    > .MuiOutlinedInput-input {
      color: ${({ disabled, readOnly }) => (disabled && !readOnly ? COLORS.text.disabled : COLORS.text.primary)};
      -webkit-text-fill-color: currentColor;
    }

    > .MuiOutlinedInput-notchedOutline,
    > .MuiOutlinedInput-notchedOutline:disabled,
    &.Mui-disabled > .MuiOutlinedInput-notchedOutline {
      border-color: ${COLORS.components.input.enabledBorder};
      ${({ severity, disabled, readOnly }) =>
        severity && !disabled && !readOnly && `border-color: ${COLORS.validation[severity].main}`};
      ${({ readOnly }) => readOnly && `border-color: ${COLORS.components.input.readonlyBorder}`};
    }

    &:hover > .MuiOutlinedInput-notchedOutline {
      ${({ disabled, readOnly }) => !disabled && !readOnly && `border-color: ${COLORS.components.input.hoverBorder}`};
    }

    &.Mui-focused > .MuiOutlinedInput-notchedOutline {
      border-color: ${({ severity }) => (severity ? COLORS.validation[severity].main : COLORS.primary.main)};
    }
  }

  > .MuiInputLabel-root,
  > .MuiInputLabel-root:disabled,
  > .MuiInputLabel-root.Mui-disabled,
  > * > .MuiInputLabel-root,
  > * > .MuiInputLabel-root:disabled,
  > * > .MuiInputLabel-root.Mui-disabled,
  > * > * > .MuiInputLabel-root,
  > * > * > .MuiInputLabel-root:disabled,
  > * > * > .MuiInputLabel-root.Mui-disabled {
    color: ${({ severity, disabled, readOnly }) =>
      severity && !disabled && !readOnly ? COLORS.validation[severity].main : COLORS.text.secondary};
    ${({ disabled, readOnly }) => disabled && !readOnly && `color: ${COLORS.text.disabled}`};
    &.Mui-focused {
      color: ${({ severity }) => (severity ? COLORS.validation[severity].main : COLORS.primary.main)};
    }
  }

  &:has(.MuiFormGroup-root) {
    padding-top: 24px;
    label.MuiFormLabel-root {
      transform: translate(0, 0);
    }
  }

  .Mui-error {
    color: ${COLORS.validation.error.main};
    > .MuiOutlinedInput-notchedOutline {
      border-color: ${COLORS.validation.error.main};
    }
  }

  > .MuiInputLabel-root:not(.MuiInputLabel-shrink) {
    ${({ $hasPrefix }) => $hasPrefix && `transform: translate(41px, 8px); padding-right: 25px;`};
  }
`

const NovaFormField: MsdkStyledComponent<MsdkFormFieldProps> = styled(MuiFormControl, {
  shouldForwardProp: (prop) => prop !== '$hasPrefix' && prop !== 'severity' && prop !== 'readOnly',
})<{
  $hasPrefix?: boolean
  severity?: keyof typeof COLORS.validation
  readOnly?: boolean
}>`
  width: 100%;
  padding-right: ${uiTokens.spacing.padding.large}px;

  .MuiInputBase-root {
    min-height: 48px;
  }
  .MuiInputLabel-root:not(.MuiInputLabel-shrink) {
    color: ${uiTokens.uiContent.accent};
    transform: translate(16px, 13px) scale(1);
  }
  /* override label with nova style - to define */
  .MuiInputLabel-root.MuiInputLabel-shrink,
  .MuiInputLabel-root.Mui-focused {
    font-size: ${uiTokens.font.fontSize.uiControlText.medium}px;
    font-family: ${uiTokens.font.fontFamily.default};
    font-style: normal;
    font-weight: ${uiTokens.font.fontWeight.regular};
    line-height: ${uiTokens.font.lineHeight.uiControlText.medium}px;
    letter-spacing: ${uiTokens.font.letterSpacing.normal};
    transform: translate(14px, -9px) scale(1);
  }
  .MuiOutlinedInput-notchedOutline legend > span {
    font-size: ${uiTokens.font.fontSize.uiControlText.medium}px;
  }
  .MuiInputAdornment-root {
    color: ${uiTokens.uiContent.accent};
  }

  > .MuiOutlinedInput-root,
  > * > .MuiOutlinedInput-root,
  > * > * > .MuiOutlinedInput-root {
    border-radius: ${uiTokens.radius.large}px;
    background-color: ${COLORS.components.input.background};
    &:hover:not(.Mui-focused) {
      ${({ disabled, readOnly }) =>
        !disabled &&
        !readOnly &&
        `
        background-color: ${uiTokens.uiControl.outlined.hover};
      `};
    }

    > .MuiOutlinedInput-input {
      color: ${({ disabled, readOnly }) => (disabled && !readOnly ? COLORS.text.disabled : COLORS.text.primary)};
      -webkit-text-fill-color: currentColor;
      font-family: ${uiTokens.font.fontFamily.default};
    }

    > .MuiOutlinedInput-notchedOutline,
    > .MuiOutlinedInput-notchedOutline:disabled,
    &.Mui-disabled > .MuiOutlinedInput-notchedOutline {
      border-color: ${COLORS.components.input.enabledBorder};

      ${({ severity, disabled, readOnly }) =>
        severity &&
        severity !== 'none' &&
        !disabled &&
        !readOnly &&
        `border-color: ${COLORS.validation[severity].main}`};
      ${({ readOnly }) => readOnly && `border-color: ${COLORS.components.input.readonlyBorder}`};
    }

    &:hover > .MuiOutlinedInput-notchedOutline {
      ${({ disabled, readOnly }) =>
        !disabled &&
        !readOnly &&
        `
        border-color: ${COLORS.components.input.hoverBorder};
      `};
    }

    &.Mui-focused > .MuiOutlinedInput-notchedOutline {
      border-color: ${({ severity }) =>
        severity && severity !== 'none' ? COLORS.validation[severity].main : COLORS.primary.main};
    }
  }

  > .MuiInputLabel-root,
  > .MuiInputLabel-root:disabled,
  > .MuiInputLabel-root.Mui-disabled,
  > * > .MuiInputLabel-root,
  > * > .MuiInputLabel-root:disabled,
  > * > .MuiInputLabel-root.Mui-disabled,
  > * > * > .MuiInputLabel-root,
  > * > * > .MuiInputLabel-root:disabled,
  > * > * > .MuiInputLabel-root.Mui-disabled {
    color: ${({ severity, disabled, readOnly }) =>
      severity && severity !== 'none' && !disabled && !readOnly
        ? COLORS.validation[severity].main
        : uiTokens.uiContent.neutral};
    ${({ disabled, readOnly }) => disabled && !readOnly && `color: ${COLORS.text.disabled}`};
    &.Mui-focused {
      color: ${({ severity }) =>
        severity && severity !== 'none' ? COLORS.validation[severity].main : COLORS.primary.main};
    }
  }

  .MuiFormLabel-asterisk,
  .MuiInputLabel-asterisk {
    color: ${uiTokens.uiContent.error};
  }

  &:hover > .MuiInputLabel-root {
    color: ${({ severity, disabled, readOnly }) =>
      severity && severity !== 'none' && !disabled && !readOnly ? undefined : uiTokens.uiStroke.accent};
  }

  &:has(.MuiFormGroup-root) {
    padding-top: 24px;
    label.MuiFormLabel-root {
      transform: translate(0, 0);
    }
  }

  .Mui-error {
    color: ${COLORS.validation.error.main};
    > .MuiOutlinedInput-notchedOutline {
      border-color: ${COLORS.validation.error.main};
    }
  }

  > .MuiInputLabel-root:not(.MuiInputLabel-shrink) {
    ${({ $hasPrefix }) => $hasPrefix && `transform: translate(41px, 12px); padding-right: 25px;`};
    font-family: ${uiTokens.font.fontFamily.default};
  }
`

export const FormField = createComponentWrapperWithRef<MsdkFormFieldProps>(
  {
    mui: MuiFormField,
    nova: NovaFormField,
  },
  'FormField',
)
